import round from 'lodash.round';
import clamp from 'lodash.clamp';

import { convert, svgRootFromText } from 'js/shared/lib/SVGConvert';
import svgSplitter from 'js/playback/helpers/svgSplitter';
import { generateFallbackRevealPath } from 'js/playback/helpers/generateFallbackRevealPath';
import { DRAW_SPEED, MIN_DEFAULT_ANIMATION_TIME, MAX_DEFAULT_ANIMATION_TIME } from 'js/config/defaults';
import {
  defaultAnimationTime,
  animationTime as IMAGE_DEFAULT_ANIMATION_TIME
} from 'js/shared/resources/scribedefaults';
import { FILE_CONTENT_TYPES } from 'js/config/consts';
import ScribeImageElementModel from 'js/models/ScribeImageElementModel';

import { getImagePixelsFromUrl } from './getImagePixelsFromUrl';

function getSvgPathsDistance(paths: NodeListOf<SVGGeometryElement>) {
  return Array.from(paths).reduce((acc, path) => {
    return acc + path.getTotalLength?.();
  }, 0);
}

async function getPixelsFromFile({ file }: { file: File }) {
  const url = URL.createObjectURL(file);
  const pixels = await getImagePixelsFromUrl({ url });

  URL.revokeObjectURL(url);

  return pixels;
}

async function getFallbackRevealPathLength(width: number, height: number, file: File) {
  const pixels = await getPixelsFromFile({ file });
  const [fallbackPath] = await generateFallbackRevealPath({
    element: { width, height } as ScribeImageElementModel,
    pixels
  });
  const { totalLength } = fallbackPath;
  return totalLength;
}

export default async function getSvgDefaultAnimationTimingFromFile(file: File, width: number, height: number) {
  if (file.type !== FILE_CONTENT_TYPES.SVG) {
    return;
  }

  const svgText = await file.text();
  const svg = svgRootFromText(svgText);

  if (!svg) {
    return defaultAnimationTime;
  }

  const svgString = convert(svg.outerHTML);
  const svgParts = svgSplitter(svgString);

  const strokesDistance = getSvgPathsDistance(svgParts.strokes);

  if (strokesDistance === 0) {
    return IMAGE_DEFAULT_ANIMATION_TIME;
  }

  const revealsDistance =
    !svgParts.revealPaths.length && !!svgParts.fills.length
      ? await getFallbackRevealPathLength(width, height, file)
      : getSvgPathsDistance(svgParts.revealPaths);

  const totalDistance = strokesDistance + revealsDistance;

  const animationTime = round(totalDistance / DRAW_SPEED, 1);

  return clamp(animationTime, MIN_DEFAULT_ANIMATION_TIME, MAX_DEFAULT_ANIMATION_TIME);
}
